import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, LinearProgress, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import useApi from "../../../../hooks/useApi";
import usePifReferenceData from "../../../../hooks/usePifReferenceData";
import usePifPositionRules from "../../../../hooks/usePifPositionRules";
import usePifSettings from "../../../../hooks/usePifSettings";
import { getEstDateBySameDayFormat } from "../../../../utils/dateHelper";
import { savePostIfAnswers, savePostIfApplicant } from "../../../../services/pifService";
import { POSTIF_RESULT_CODE } from "../../../../services/pifWriteResult";
import {
  PostIfAttributeValueDto,
  PostIfEmploymentHistoryDto,
  PostIfResultDto,
  SavePostIfAnswersRequestDto,
  SavePostIfApplicantRequestDto,
} from "../../../../types/pifDto";
import PifSectionApplicant from "./sections/PifSectionApplicant";
import PifSectionContact from "./sections/PifSectionContact";
import PifSectionPosition from "./sections/PifSectionPosition";
import PifSectionPay from "./sections/PifSectionPay";
import PifSectionDynamic from "./sections/PifSectionDynamic";
import PifOfferRail from "./PifOfferRail";
import PifReviewDrawer from "./PifReviewDrawer";
import { getPifGaps, getPifRequiredTotal, isFullTimeAllowed, isStorableDecimal } from "../pifValidation";
import { HAMASPIK_COLORS } from "../../../../assets/hamaspik/theme";
import Badge, { PifBadgeVariant } from "../../../../assets/hamaspik/Badge";
import useContainerWidth from "../../../../hooks/useContainerWidth";
import { SHELL_HEADER_HEIGHT } from "../pifLayout";
// PifDepartments is gone — the nested fixture shape it described was replaced by the
// id-based reference-data lookups.
import {
  PifCandidate,
  PifDraft,
  PifEavValues,
  PifFlow,
  PifFormValues,
  PifSectionId,
  isBridgedFieldKey,
} from "../types";
import "./PifWorkspace.scss";

const WORKSPACE_TWO_COL = 980;

/**
 * Kind-of-hire label -> Portal_PostIF.SwitchOrAddition (VARCHAR(20)), so the full label is never
 * sent to that column. "New employee" maps to null: it is neither a switch nor an addition, and
 * the draft already records it as Flow = "new-hire".
 * Keys must match HIRE_TYPES in PifSectionPosition.tsx.
 */
const HIRE_TYPE_TO_SWITCH_OR_ADDITION: Record<string, string | null> = {
  "New employee": null,
  "Additional position (keeps current one)": "addition",
  "Position change (replaces current one)": "switch",
};

/** Stable test hook per missing-answer link, e.g. "Kind of hire" → postif-missing-link-kind-of-hire. */
const slugify = (label: string): string =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Epic 79822's EMAIL_REQUIRED placeholder is gone: the toggle it stood in for is now read
// for real from the System_Settings_New "PostIF" category via usePifSettings.

/** The dynamic section only exists when the API gave us a field catalogue. */
const DETAILS_SECTION: { id: PifSectionId; label: string } = { id: "details", label: "Additional details" };

const FLOW_LABEL: Record<PifFlow, string> = { default: "", new: "NEW HIRE", active: "EXISTING EMPLOYEE", inactive: "REHIRE" };
const FLOW_BADGE_VARIANT: Record<PifFlow, PifBadgeVariant> = { default: "neutral", new: "success", active: "primary", inactive: "warning" };

const SECTIONS: { id: PifSectionId; label: string }[] = [
  { id: "applicant", label: "Applicant" },
  { id: "contact", label: "Contact" },
  { id: "position", label: "Position" },
  { id: "pay", label: "Pay" },
];

interface PifWorkspaceProps {
  flow: PifFlow;
  candidate: PifCandidate | null;
  values: PifFormValues;
  draft: PifDraft | null;
  /**
   * Writes the Portal_PostIF row if this is the first save, and answers the id to save against.
   * Null means the create failed and nothing should be written - see usePifDraft.ensureCreated.
   */
  ensureDraftId: () => Promise<number | null>;
  /** The dynamic field catalogue, from GET api/postif/attributes (or /{postIfId} on a reopen). */
  catalogue: PostIfAttributeValueDto[];
  eav: PifEavValues;
  history: PostIfEmploymentHistoryDto[];
  onChange: (patch: Partial<PifFormValues>) => void;
  onEavChange: (fieldKey: string, value: string) => void;
  /** Everything typed is now persisted, so the leave-without-saving prompt can stand down. */
  onSaved: () => void;
  onExit: () => void;
  onSigned: (refNo: string, signedAt: string) => void;
}

const PifWorkspace: FC<PifWorkspaceProps> = ({
  flow,
  candidate,
  values: f,
  draft,
  ensureDraftId,
  catalogue,
  eav,
  history,
  onChange,
  onEavChange,
  onSaved,
  onExit,
  onSigned,
}) => {
  const { executeApi } = useApi();
  const { emailRequired } = usePifSettings();
  const { departments, positions, reportToUsers, services, clients, rateTypes } = usePifReferenceData({
    departmentId: f.deptId,
    serviceId: f.serviceId,
  });
  const { rules } = usePifPositionRules({ departmentId: f.deptId, positionId: f.posId });

  // "Full-time" can arrive before any position is chosen — PostInterviewForm seeds it from the
  // employee's active employment-history row — and a position switch can invalidate a choice that
  // was legal a moment ago. The FT/PT toggle only goes disabled in that case, keeping the stale
  // value on screen and in the saved answer, so clamp it here instead. Guarded on `rules` being
  // loaded: null means "no position yet", which is not a verdict that full-time is disallowed.
  useEffect(() => {
    if (rules && !isFullTimeAllowed(rules) && f.empType === "Full-time") onChange({ empType: "Part-time" });
  }, [rules, f.empType, onChange]);
  // Layout mechanics from Epic 79822: width comes from the container, not the window, and
  // the sticky header's height is measured rather than assumed.
  const [rootRef, width] = useContainerWidth<HTMLDivElement>();
  const headerRef = useRef<HTMLDivElement>(null);
  const spyPausedUntil = useRef(0);
  const [activeSection, setActiveSection] = useState<PifSectionId>("applicant");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [attested, setAttested] = useState(false);
  const [draftStamp, setDraftStamp] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [rejected, setRejected] = useState<Record<string, string>>({});
  // The departments/buildings/supervisors fixture state and its loader are gone — that data
  // now comes from usePifReferenceData's id-based lookups above.

  /**
   * The nav steps. The dynamic section only exists once the API returns a field catalogue, so
   * it is appended rather than declared in SECTIONS — otherwise the stepper would show a
   * "Additional details" pill that scrolls nowhere.
   */
  const sections = useMemo(
    () => (catalogue.length ? [...SECTIONS, DETAILS_SECTION] : SECTIONS),
    [catalogue.length],
  );

  // The sticky header wraps on narrow widths, so its height has to be measured rather than
  // assumed — scroll targets and the cards' scroll-margin-top both read this variable.
  useEffect(() => {
    const root = rootRef.current;
    const header = headerRef.current;
    if (!root || !header) return;

    const applyHeaderHeight = () => {
      root.style.setProperty("--pif-header-height", `${header.offsetHeight}px`);
    };
    applyHeaderHeight();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(applyHeaderHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, [rootRef]);

  const headerOffset = () => SHELL_HEADER_HEIGHT + (headerRef.current?.offsetHeight ?? 0) + 8;

  useEffect(() => {
    const onScroll = () => {
      // A smooth scroll fires dozens of events on the way to the target; letting them set the
      // active pill would fight the one the user just clicked.
      if (Date.now() < spyPausedUntil.current) return;

      const doc = document.documentElement;
      const atBottom = window.scrollY + window.innerHeight >= doc.scrollHeight - 2;
      if (atBottom) {
        // The last section can never clear the header line — the page runs out of scroll first.
        setActiveSection(sections[sections.length - 1].id);
        return;
      }

      const line = headerOffset() + 4;
      let current = sections[0].id;
      sections.forEach(({ id }) => {
        const el = document.getElementById(`sec-${id}`);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      });
      setActiveSection(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // Re-subscribed when the dynamic section appears, so scrolling into it lights its pill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  // A reopened draft restores Department and Position as ids from the draft header, but the
  // missing-answer check keys off the LABELS (need(f.dept), need(f.pos)) - so without these two
  // backfills both dropdowns render correctly yet are still reported as unanswered.
  useEffect(() => {
    if (f.deptId === null || f.dept || !departments.length) return;
    const match = departments.find((d) => d.id === f.deptId);
    if (match?.text) onChange({ dept: match.text });
  }, [f.deptId, f.dept, departments, onChange]);

  useEffect(() => {
    if (f.posId === null || f.pos || !positions.length) return;
    const match = positions.find((pos) => pos.id === f.posId);
    if (match?.text) onChange({ pos: match.text });
  }, [f.posId, f.pos, positions, onChange]);

  useEffect(() => {
    if (f.reportsToId === null || f.reportsTo || !reportToUsers.length) return;
    const match = reportToUsers.find((u) => u.id === f.reportsToId);
    if (match?.text) onChange({ reportsTo: match.text });
  }, [f.reportsToId, f.reportsTo, reportToUsers, onChange]);

  // Same for service and its dependent client. Order matters and resolves itself: serviceId is
  // restored first, usePifReferenceData then fetches that service's clients, and this effect fills
  // the client label once that list arrives.
  useEffect(() => {
    if (f.serviceId === null || f.service || !services.length) return;
    const match = services.find((s) => s.id === f.serviceId);
    if (match?.text) onChange({ service: match.text });
  }, [f.serviceId, f.service, services, onChange]);

  useEffect(() => {
    if (f.clientId === null || f.client || !clients.length) return;
    const match = clients.find((c) => c.id === f.clientId);
    if (match?.text) onChange({ client: match.text });
  }, [f.clientId, f.client, clients, onChange]);

  // A reopened draft restores rate_type as an id from EAV, but its label lives in the reference
  // lookup this screen owns. Backfill it: the Offer rail renders the label, and the missing-answer
  // check keys off f.rateType, so without this a resumed draft still reports "Rate type" missing.
  useEffect(() => {
    if (f.rateTypeId === null || f.rateType || !rateTypes.length) return;
    const match = rateTypes.find((r) => r.id === f.rateTypeId);
    if (match?.text) onChange({ rateType: match.text });
  }, [f.rateTypeId, f.rateType, rateTypes, onChange]);

  const goto = (id: PifSectionId) => {
    setReviewOpen(false);
    setActiveSection(id);
    setTimeout(() => {
      const el = document.getElementById(`sec-${id}`);
      if (el) {
        spyPausedUntil.current = Date.now() + 900;
        window.scrollTo({
          top: el.getBoundingClientRect().top + window.scrollY - headerOffset(),
          behavior: "smooth",
        });
      }
    }, 60);
  };

  /**
   * Writes both halves of the record: the fixed applicant columns and the EAV answers.
   * Resolves true only when everything landed, so submit can refuse to run on a failure.
   *
   * The applicant call runs for EVERY flow, including an active employee. sProc_Portal_
   * PostIFApplicantsIU's header says the active flow "sets Portal_PostIF.
   * EnterpriseEmployeeId directly and never calls this proc" — but no endpoint writes that
   * column, and sProc_Portal_PostIF_Submit_U requires PostIFApplicantId to be non-null for
   * every flow. Skipping it makes an active-employee hire impossible to submit, so we write
   * the row (name + EnterpriseEmployeeId, never an SSN). Revisit if the API adds a header write.
   */
  // Answers the id everything was saved against - null on any failure. The review drawer submits
  // against this rather than a pre-existing draft.postIfId, which is null until this first runs
  // (Bug 79824: the row is created here, not when the form opened).
  const persistDraft = useCallback(async (): Promise<number | null> => {
    if (!draft) return null;
    setRejected({});

    // The row is written here, on the first save - not when the form opened (Bug 79824). Every
    // write below addresses this id, so a failed create must abort the save rather than post
    // against a record that does not exist.
    const postIfId = await ensureDraftId();
    if (!postIfId) return null;

    const applicant: SavePostIfApplicantRequestDto = {
      postIfId,
      firstName: f.firstName || null,
      middleName: f.middleName || null,
      lastName: f.lastName || null,
      knownName: f.knownAs || null,
      dob: f.dob || null,
      address1: f.addr1 || null,
      city: f.city || null,
      state: f.state || null,
      zip: f.zip || null,
      homePhone: f.home || null,
      cell: f.cell || null,
      email: f.email || null,
      enterpriseEmployeeId: candidate?.employeeId ?? null,
      // Portal_PostIF.DeptPositionId / SwitchOrAddition: create-draft sends the position as null
      // because it is chosen here, so this is its only write path. Until it lands, the API cannot
      // resolve the hiring band and its rate-band check silently no-ops.
      deptPositionId: f.posId ?? null,
      departmentId: f.deptId ?? null,
      switchOrAddition: HIRE_TYPE_TO_SWITCH_OR_ADDITION[f.hireType] ?? null,
    };
    const saved = await executeApi<SavePostIfApplicantRequestDto, PostIfResultDto>(savePostIfApplicant, applicant);
    if (!saved) return null;

    // Only send fields the catalogue actually knows about — an unknown fieldKey has no
    // attribute id to write against and the API rejects the whole batch on one bad row.
    //
    // Empty strings must go as null. sProc_Portal_PostIFAttributeValues_IU validates
    // `WHERE Value IS NOT NULL`, and '' is not null: TRY_CONVERT(DATE,'') and
    // TRY_CONVERT(DECIMAL,'') both yield NULL, so a touched-then-cleared date or rate field
    // was rejected as "Invalid date/numeric value" — and because rejection is atomic, it
    // silently discarded every other answer in the batch. Null means "no answer", which is
    // also what clearing a field should do.
    // Three keys in the seeded catalogue (Epic-79819-Seed-PostIFAttributes.sql) have a hardcoded
    // control in this form writing to `f` instead of `eav`, so their values never reached
    // save-answers at all. Bridge them here; the empty-string-to-null rule above then applies to
    // them too, so clearing one clears the stored answer. An explicit `eav` entry always wins,
    // and the attributeId filter drops any key the catalogue does not define.
    // Only null and 0 mean "nothing selected". NEGATIVE IDS ARE VALID: sProc_System_Lists_RateTypes_S
    // hardcodes -1 for 'Acuity-based', so treating <= 0 as absent silently dropped that choice.
    // 0 is still excluded - it is the artefact a cleared Select used to produce via Number("").
    const id = (value: number | null): string => (value === null || value === 0 ? "" : String(value));

    const derived: PifEavValues = {
      requested_rate: f.rate,
      desired_start_date: f.startDate,
      department_notes: f.notes,
      hours_per_week: f.hours,
      // The label ("Part-time"/"Full-time"), which is what the list sprocs read straight into the
      // grid's Type column. Always set — the toggle has no empty state.
      employment_type: f.empType,
      // The id, never the label, so the stored value stays a real foreign key.
      rate_type: id(f.rateTypeId),
      service: id(f.serviceId),
      client: id(f.clientId),
      reports_to: id(f.reportsToId),
    };

    // Second line of defence for the two free-text numeric fields. A value the API cannot parse
    // fails validation and, because save-answers rejects the whole batch atomically, would stop
    // every other answer from saving. Drop just the bad key: getPifGaps already flags it as
    // "must be a number", and omitting it (rather than sending null) leaves any previously
    // stored value intact instead of wiping it.
    for (const key of ["requested_rate", "hours_per_week"] as const) {
      const value = derived[key];
      if (String(value ?? "").trim() !== "" && !isStorableDecimal(value)) delete derived[key];
    }

    const merged: PifEavValues = { ...derived, ...eav };

    // A key with no Portal_PostIFAttributes row has no attribute id to write against, so the
    // filter below discards it SILENTLY - which reads as "the field is not saving". Surface it
    // in development: it means the seed script has not been run, or this draft was opened
    // before the attribute was seeded (attributeIdByFieldKey is captured once, at draft open).
    if (process.env.NODE_ENV !== "production") {
      const unmapped = Object.keys(merged).filter(
        (key) => isBridgedFieldKey(key) && !draft.attributeIdByFieldKey[key],
      );
      if (unmapped.length) {
        console.warn(
          "[PostIF] Dropped answers - no Portal_PostIFAttributes row for: " +
            unmapped.join(", ") +
            ". Re-run the PostIFAttributes seed scripts against the Portal DB (Epic-79819-Seed-PostIFAttributes.sql, " +
            "Epic-79820-...-BridgedFields.sql, Epic-79822-...-EmploymentType.sql), then reload the draft - " +
            "attributeIdByFieldKey is captured once, when the draft is opened.",
        );
      }
    }

    const answers = Object.entries(merged)
      .map(([fieldKey, value]) => ({
        postIfAttributeId: draft.attributeIdByFieldKey[fieldKey],
        value: String(value ?? "").trim() === "" ? null : value,
      }))
      .filter((a) => !!a.postIfAttributeId);

    if (answers.length) {
      const result = await executeApi<SavePostIfAnswersRequestDto, PostIfResultDto>(savePostIfAnswers, {
        postIfId,
        answers,
      });
      if (!result) return null;
      if (result.rejected?.length) {
        setRejected(
          result.rejected.reduce<Record<string, string>>((map, r) => {
            if (r.fieldKey) map[r.fieldKey] = r.reason || "The API rejected this value.";
            return map;
          }, {}),
        );
        return null;
      }
      // -2 resolves with its rejected rows rather than throwing (see readPostIfWrite), so a
      // refusal that named no field would otherwise fall through and be stamped as saved.
      if (result.code !== POSTIF_RESULT_CODE.SUCCESS) return null;
    }

    setDraftStamp(getEstDateBySameDayFormat(new Date()));
    onSaved();
    return postIfId;
  }, [executeApi, draft, ensureDraftId, f, eav, candidate?.employeeId, onSaved]);

  const handleSaveDraft = async () => {
    setSaving(true);
    await persistDraft();
    setSaving(false);
  };


  const gaps = useMemo(
    () => getPifGaps(f, flow, rules, emailRequired, catalogue, eav),
    [f, flow, rules, emailRequired, catalogue, eav],
  );
  const total = getPifRequiredTotal(flow, rules, emailRequired, catalogue, f.hasLicense);
  const done = Math.max(0, total - gaps.length);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const steps = useMemo(
    () =>
      sections.map(({ id, label }) => {
        const missing = gaps.filter((gap) => gap.sec === id).length;
        return { id, label, missing, complete: missing === 0 };
      }),
    [gaps, sections],
  );

  const two = width >= WORKSPACE_TWO_COL;

  const name = `${f.firstName} ${f.lastName}`.trim();
  const initials = candidate && candidate.first && candidate.last
    ? `${candidate.first[0]}${candidate.last[0]}`
    : name
      ? name.split(" ").map((x) => x[0]).join("").slice(0, 2)
      : "+";

  const gridClass = `pif-workspace__grid${two ? " pif-workspace__grid--two" : ""}`;
  const railClass = `pif-workspace__rail${two ? " pif-workspace__rail--sticky" : ""}`;

  return (
    <Box className="pif-workspace" ref={rootRef}>
      <Box className="pif-workspace__header" ref={headerRef}>
        <Box className="pif-workspace__header-left">
          <Avatar
            src={candidate?.profileImage || undefined}
            alt={name || "Applicant"}
            sx={{ width: 30, height: 30, bgcolor: "secondary.main", color: "#fff", fontWeight: 700, fontSize: 12, flex: "none" }}
          >
            {initials}
          </Avatar>
          <Box className="pif-workspace__name-row">
            <Typography className="pif-workspace__name">{name || "New applicant"}</Typography>
            {FLOW_LABEL[flow] && (
              <Badge variant={FLOW_BADGE_VARIANT[flow]} size="sm">
                {FLOW_LABEL[flow]}
              </Badge>
            )}
          </Box>
        </Box>

        <Box component="nav" className="pif-workspace__nav" aria-label="Form sections">
          <Box component="ol" className="pif-workspace__nav-track">
            {steps.map(({ id, label, missing, complete }, i) => {
              const active = activeSection === id;
              // The connector leading into this step fills once the step behind it is answered,
              // so the rail reads as one line advancing left to right.
              const linked = i > 0 && steps[i - 1].complete;
              const state = complete ? "is-complete" : "is-pending";
              return (
                <Box
                  component="li"
                  key={id}
                  className={`pif-workspace__nav-step ${linked ? "is-linked" : ""}`}
                >
                  <button
                    type="button"
                    className={`pif-workspace__nav-item ${state} ${active ? "is-active" : ""}`}
                    aria-current={active ? "step" : undefined}
                    aria-label={complete ? `${label} — complete` : `${label} — ${missing} answer${missing === 1 ? "" : "s"} needed`}
                    title={complete ? `${label} — complete` : `${label} — ${missing} still needed`}
                    onClick={() => goto(id)}
                  >
                    <span className="pif-workspace__nav-dot" aria-hidden="true">
                      {complete ? <CheckRoundedIcon sx={{ fontSize: 13 }} /> : missing}
                    </span>
                    <span className="pif-workspace__nav-label">{label}</span>
                  </button>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box className={gridClass}>
        <Box className="pif-workspace__sections">
          <PifSectionApplicant flow={flow} values={f} onChange={onChange} />
          <PifSectionContact
            flow={flow}
            emailRequired={emailRequired}
            mayOperateVehicle={!!rules?.mayOperateVehicle}
            values={f}
            onChange={onChange}
          />
          <PifSectionPosition
            values={f}
            onChange={onChange}
            rules={rules}
            departments={departments}
            positions={positions}
            reportToUsers={reportToUsers}
            services={services}
            clients={clients}
          />
          <PifSectionPay values={f} onChange={onChange} rules={rules} rateTypes={rateTypes} />
          <PifSectionDynamic catalogue={catalogue} values={eav} onChange={onEavChange} rejected={rejected} />
        </Box>

        <Box className={railClass}>
          <PifOfferRail values={f} flow={flow} rules={rules} history={history} />
        </Box>
      </Box>

      <Box className="pif-workspace__footer">
        <LinearProgress
          variant="determinate"
          value={pct}
          color={pct === 100 ? "success" : "primary"}
          sx={{
            height: 3,
            borderRadius: 0,
            bgcolor: HAMASPIK_COLORS.neutral100,
            "& .MuiLinearProgress-bar": { transition: "transform 220ms ease" },
          }}
        />
        <Box className="pif-workspace__footer-inner">
          <Box className="pif-workspace__footer-status">
            <Typography className="pif-workspace__footer-title">
              {gaps.length === 0
                ? "Ready to review & sign"
                : `${gaps.length} answer${gaps.length === 1 ? "" : "s"} still needed`}
              <Box component="span" className="pif-workspace__footer-count" data-testid="postif-missing-count">
                {done}/{total}
              </Box>
            </Typography>
            <Typography variant="caption" className="pif-workspace__footer-detail" component="div">
              {gaps.length ? (
                <>
                  Left:{" "}
                  {gaps.slice(0, 3).map((g, i) => (
                    <React.Fragment key={g.label}>
                      {i > 0 && ", "}
                      <Box
                        component="span"
                        role="button"
                        tabIndex={0}
                        className="pif-workspace__footer-link"
                        data-testid={`postif-missing-link-${slugify(g.label)}`}
                        onClick={() => goto(g.sec)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            goto(g.sec);
                          }
                        }}
                      >
                        {g.label}
                      </Box>
                    </React.Fragment>
                  ))}
                  {gaps.length > 3 ? ` +${gaps.length - 3} more` : ""}
                </>
              ) : (
                "Everything required is answered."
              )}
            </Typography>
          </Box>
          <Box className="pif-workspace__footer-state">
            <Badge variant={draftStamp ? "success" : "warning"} size="md">
              {draftStamp ? `Draft saved ${draftStamp}` : "Not saved yet"}
            </Badge>
          </Box>
          <Box className="pif-workspace__footer-actions">
            <Button
              variant="outlined"
              size="medium"
              onClick={onExit}
              className="pif-workspace__footer-btn"
              data-testid="postif-exit"
            >
              Cancel
            </Button>
            <Button
              variant="text"
              color="secondary"
              size="medium"
              onClick={handleSaveDraft}
              disabled={!draft || saving}
              className="pif-workspace__footer-btn"
              data-testid="postif-save-draft"
            >
              {saving ? "Saving…" : "Save draft"}
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={() => setReviewOpen(true)}
              className="pif-workspace__footer-btn pif-workspace__footer-btn--primary"
              data-testid="postif-review"
            >
              {gaps.length ? "Review" : "Review & sign"}
            </Button>
          </Box>
        </Box>
      </Box>

      <PifReviewDrawer
        open={reviewOpen}
        gaps={gaps}
        values={f}
        onPersist={persistDraft}
        attested={attested}
        onToggleAttest={() => setAttested((v) => !v)}
        onClose={() => setReviewOpen(false)}
        onJump={goto}
        onSigned={onSigned}
      />
    </Box>
  );
};

export default PifWorkspace;
